/**
 * usePendingChannelDrops — stages slot drops locally instead of writing to the
 * DB on each drop. Users can stack drops across multiple channels/slots and
 * then commit them all via a single Save (or throw the whole batch away via
 * Discard) from the bottom bar.
 *
 * 2026-05-21 v2 user-bug fix: adds an explicit **reorderPlan per channel** —
 * the final slot order for one channel after all pending mutations. The plan
 * is lazily seeded from the TanStack QueryCache (no more `snapshot` prop
 * drilling / race). The legacy `shifts`-based path remains for backward
 * compatibility with callers that still pass an explicit
 * `currentSlotsByChannel` snapshot.
 *
 * Save pipeline → `runPendingSave.ts`; planning helpers → `pendingDropHelpers`
 * + `pendingPlanHelpers`; hook callbacks → `usePendingCallbacks`. The split
 * exists so this file stays inside the 200-line FE limit.
 */
import { useCallback, useMemo, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  groupShiftsByChannel,
  type ChannelSnapshot,
  type PendingShift,
} from './buildReorderPlan'
import { deriveMaps } from './pendingDropHelpers'
import type { ChannelReorderPlans } from './channelReorderPlan'
import type { AppliedDelete } from './applyDelete'
import { runSave } from './runPendingSave'
import {
  useAddCallback,
  useRemoveCallback,
  useReorderSlotsCallback,
  useRequestDeleteCallback,
} from './usePendingCallbacks'
import type { AddToProductImage, AddToProductTarget } from '../types'

export interface PendingDrop {
  image: AddToProductImage
  target: AddToProductTarget
  /** Stable temp key reused by the save pipeline to resolve plan-new → linkId. */
  tempKey: string
}

export type CurrentSlotsByChannel = Map<string, ChannelSnapshot>

export interface PendingChannelDrops {
  pending: Map<string, PendingDrop>
  pendingByChannelSlot: Map<string, Map<number, AddToProductImage>>
  pendingByAiRefSlot: Map<number, AddToProductImage>
  affectedChannels: Set<string>
  affectedImages: Set<string>
  shifts: PendingShift[]
  pendingShiftsByChannel: Map<string, PendingShift[]>
  reorderPlans: ChannelReorderPlans
  deletes: AppliedDelete[]
  add: (
    image: AddToProductImage,
    target: AddToProductTarget,
    currentSlotsByChannel?: CurrentSlotsByChannel,
  ) => void
  reorderSlots: (channelId: string, fromSlot: number, toSlot: number) => void
  remove: (target: AddToProductTarget) => void
  requestDelete: (target: AddToProductTarget) => void
  clear: () => void
  save: () => Promise<void>
  saving: boolean
  hasChanges: boolean
}

export interface PendingState {
  pending: Map<string, PendingDrop>
  setPending: Dispatch<SetStateAction<Map<string, PendingDrop>>>
  shifts: PendingShift[]
  setShifts: Dispatch<SetStateAction<PendingShift[]>>
  deletes: AppliedDelete[]
  setDeletes: Dispatch<SetStateAction<AppliedDelete[]>>
  snapshot: CurrentSlotsByChannel
  setSnapshot: Dispatch<SetStateAction<CurrentSlotsByChannel>>
  plans: ChannelReorderPlans
  setPlans: Dispatch<SetStateAction<ChannelReorderPlans>>
  saving: boolean
  setSaving: Dispatch<SetStateAction<boolean>>
}

function usePendingState(): PendingState {
  const [pending, setPending] = useState<Map<string, PendingDrop>>(new Map())
  const [shifts, setShifts] = useState<PendingShift[]>([])
  const [deletes, setDeletes] = useState<AppliedDelete[]>([])
  const [snapshot, setSnapshot] = useState<CurrentSlotsByChannel>(new Map())
  const [plans, setPlans] = useState<ChannelReorderPlans>(new Map())
  const [saving, setSaving] = useState(false)
  return {
    pending, setPending, shifts, setShifts, deletes, setDeletes,
    snapshot, setSnapshot, plans, setPlans, saving, setSaving,
  }
}

function hasPlanDirty(plans: ChannelReorderPlans): boolean {
  for (const plan of plans.values()) {
    if (plan.some((it) => it.kind === 'new')) return true
  }
  return false
}

export function usePendingChannelDrops(
  productId: string,
  onSaved?: () => void,
): PendingChannelDrops {
  const queryClient = useQueryClient()
  const s = usePendingState()
  const add = useAddCallback(s, queryClient, productId)
  const reorderSlots = useReorderSlotsCallback(s, queryClient, productId)
  const remove = useRemoveCallback(s)
  const requestDelete = useRequestDeleteCallback(s, queryClient, productId)
  const clear = useCallback(() => {
    s.setPending(new Map())
    s.setShifts([])
    s.setDeletes([])
    s.setPlans(new Map())
  }, [s])
  const derived = useMemo(() => deriveMaps(s.pending), [s.pending])
  const pendingShiftsByChannel = useMemo(() => groupShiftsByChannel(s.shifts), [s.shifts])
  const save = useCallback(
    () => runSave({ ...s, productId, queryClient, clear, onSaved }),
    [s, productId, queryClient, clear, onSaved],
  )
  return {
    pending: s.pending,
    ...derived,
    shifts: s.shifts,
    pendingShiftsByChannel,
    reorderPlans: s.plans,
    deletes: s.deletes,
    add,
    reorderSlots,
    remove,
    requestDelete,
    clear,
    save,
    saving: s.saving,
    hasChanges:
      s.pending.size > 0 ||
      s.shifts.length > 0 ||
      s.deletes.length > 0 ||
      hasPlanDirty(s.plans),
  }
}
